import { useMemo, useState } from "react";
import confetti from "canvas-confetti";
import { Apple, Carrot, Candy, Cookie, Milk, CupSoda, IceCreamCone, Banana, Lollipop, Egg, Droplets, Cake, Smile, Frown } from "lucide-react";

const FOODS = [
  { name: "Crunchy apple", icon: Apple, good: true, why: "Crunchy fruits and veggies help scrub teeth and make saliva, which protects them." },
  { name: "Sticky toffee", icon: Candy, good: false, why: "Sticky sweets cling to teeth for a long time, feeding sugar bugs." },
  { name: "Carrot sticks", icon: Carrot, good: true, why: "Carrots are crunchy and low in sugar - a great tiffin snack." },
  { name: "Cola", icon: CupSoda, good: false, why: "Fizzy drinks have sugar AND acid, which softens tooth enamel." },
  { name: "Milk", icon: Milk, good: true, why: "Plain milk has calcium that keeps teeth and bones strong." },
  { name: "Biscuits", icon: Cookie, good: false, why: "Biscuits turn mushy and get stuck in the grooves of back teeth." },
  { name: "Water", icon: Droplets, good: true, why: "Water washes food away and is the best drink for teeth." },
  { name: "Lollipop", icon: Lollipop, good: false, why: "Sucking a lollipop bathes teeth in sugar for a long time." },
  { name: "Boiled egg", icon: Egg, good: true, why: "Eggs have protein and vitamin D, and almost no sugar." },
  { name: "Ice cream", icon: IceCreamCone, good: false, why: "Ice cream is a treat - enjoy it with a meal and rinse with water after." },
  { name: "Banana", icon: Banana, good: true, why: "Fruit is a healthy snack. Bananas are soft, so drink water afterwards." },
  { name: "Cake", icon: Cake, good: false, why: "Cake is sugary - keep it for special days, then brush at bedtime." },
];

const shuffle = (a) => [...a].sort(() => Math.random() - 0.5);

export const SnackSorter = () => {
  const [round, setRound] = useState(0);
  const deck = useMemo(() => shuffle(FOODS).slice(0, 8), [round]); // eslint-disable-line react-hooks/exhaustive-deps
  const [i, setI] = useState(0);
  const [score, setScore] = useState(0);
  const [answer, setAnswer] = useState(null);
  const done = i >= deck.length;

  const pick = (good) => {
    if (answer !== null) return;
    const ok = good === deck[i].good;
    setAnswer(ok);
    if (ok) setScore((s) => s + 1);
  };
  const next = () => {
    setAnswer(null);
    if (i + 1 === deck.length && score >= 7) confetti({ particleCount: 120, spread: 90 });
    setI(i + 1);
  };
  const restart = () => { setRound(round + 1); setI(0); setScore(0); setAnswer(null); };

  if (done) {
    return (
      <div className="rounded-[2.5rem] bg-[#ebfbfa] p-10 text-center" data-testid="game-snack-sorter">
        <p className="font-display text-3xl font-bold text-slate-800" data-testid="snack-score">{score} / {deck.length}</p>
        <p className="mt-2 font-semibold text-slate-600">{score >= 7 ? "Snack Superstar! Your teeth say thank you." : "Good try! Play again to become a Snack Superstar."}</p>
        <button onClick={restart} data-testid="snack-restart" className="btn-pop mt-6 rounded-full bg-slate-900 px-6 py-3 font-bold text-white">Play again</button>
      </div>
    );
  }

  const f = deck[i];
  return (
    <div className="rounded-[2.5rem] bg-[#ebfbfa] p-6 sm:p-10" data-testid="game-snack-sorter">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold sm:text-3xl">Snack Sorter</h2>
        <span className="rounded-full bg-white px-3 py-1.5 text-sm font-bold">{i + 1}/{deck.length} · Score {score}</span>
      </div>
      <p className="mt-2 text-sm font-semibold text-slate-600">Is this snack a Tooth Friend or Sugar Bug food?</p>
      <div className="mx-auto mt-6 flex max-w-sm flex-col items-center rounded-[2rem] border-[3px] border-slate-800 bg-white p-8" data-testid="snack-card">
        <f.icon className="h-20 w-20 text-teal-600" />
        <p className="font-display mt-3 text-2xl font-bold text-slate-800" data-testid="snack-name">{f.name}</p>
      </div>
      {answer === null ? (
        <div className="mt-6 grid grid-cols-2 gap-3">
          <button onClick={() => pick(true)} data-testid="snack-good" className="btn-pop flex items-center justify-center gap-2 rounded-2xl bg-teal-600 py-4 font-bold text-white"><Smile className="h-5 w-5" />Tooth Friend</button>
          <button onClick={() => pick(false)} data-testid="snack-bad" className="btn-pop flex items-center justify-center gap-2 rounded-2xl bg-[#ff6b6b] py-4 font-bold text-white"><Frown className="h-5 w-5" />Sugar Bug food</button>
        </div>
      ) : (
        <div className="mt-6 rounded-2xl bg-white p-5 text-center" data-testid="snack-feedback">
          <p className={`font-display text-xl font-bold ${answer ? "text-teal-700" : "text-[#ff6b6b]"}`}>{answer ? "Correct!" : "Not quite!"}</p>
          <p className="mt-1 text-sm text-slate-600">{f.why}</p>
          <button onClick={next} data-testid="snack-next" className="btn-pop mt-4 rounded-full bg-slate-900 px-6 py-2.5 text-sm font-bold text-white">Next snack</button>
        </div>
      )}
    </div>
  );
};
